import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import palette from 'constants/palette'
import React from 'react'

type Props = {
  className?: string
  title: string
  subtitle?: string
  children: React.ReactNode
  viewButtonText?: string
  editButtonText?: string
  bgColor?: string
  viewButtonOnClick?: () => void
  // When the view target is a real URL/route, pass viewButtonHref instead of
  // viewButtonOnClick — the view control renders as an anchor (opens in a new
  // tab) so cmd/middle-click and screen readers treat it as the link it is.
  viewButtonHref?: string
  editButtonOnClick?: () => void
  'data-testid'?: string
}

const Card: React.FC<Props> = ({
  className = 'mb-m',
  title,
  subtitle,
  viewButtonText,
  viewButtonOnClick,
  viewButtonHref,
  editButtonText,
  editButtonOnClick,
  bgColor = palette.grey10,
  children,
  'data-testid': dataTestId,
}) => {
  const [css] = useStyletron()
  const viewButtonClassName = css({
    height: '32px',
    fontSize: '14px',
    marginLeft: '16px',
  })

  return (
    <div
      data-testid={dataTestId}
      className={`card p-m ${className}
      ${css({
        backgroundColor: bgColor,
      })}
      `}
    >
      <div className={`d-flex align-items-center justify-content-between`}>
        <h4 className="extra-bold mr-xs">{title}</h4>
        <div className="d-flex align-items-center">
          {viewButtonText &&
            (viewButtonHref ? (
              <Button
                $as="a"
                href={viewButtonHref}
                target="_blank"
                // rel isn't on uikit Button's prop type; forward it to the
                // underlying <a> to prevent reverse-tabnabbing on target=_blank.
                overrides={{
                  BaseButton: { props: { rel: 'noopener noreferrer' } },
                }}
                className={viewButtonClassName}
                kind="secondary"
              >
                {viewButtonText}
              </Button>
            ) : (
              <Button
                className={viewButtonClassName}
                kind="secondary"
                onClick={viewButtonOnClick}
              >
                {viewButtonText}
              </Button>
            ))}
          {editButtonText && (
            <Button
              className={css({
                height: '32px',
                fontSize: '14px',
                marginLeft: '16px',
              })}
              kind="primary"
              onClick={editButtonOnClick}
            >
              {editButtonText}
            </Button>
          )}
        </div>
      </div>
      <div className={`mb-m grey90`}>
        <Text.BodyText4>{subtitle}</Text.BodyText4>
      </div>
      {children}
    </div>
  )
}

export default Card
